import React from "react";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
 variant?: "primary" | "secondary" | "outline" | "ghost" | "danger";
 size?: "sm" | "md" | "lg";
 fullWidth?: boolean;
 loading?: boolean;
}

const variantClasses = {
 primary: "bg-store-primary text-white hover:opacity-90 active:opacity-80 hover:",
 secondary: "bg-store-secondary text-white hover:opacity-90 active:opacity-80",
 outline: "border-2 border-store-primary text-store-primary hover:bg-store-background active:bg-store-secondary active:text-white",
 ghost: "text-store-primary hover:bg-store-background active:bg-store-secondary active:text-white",
 danger: "bg-red-600 text-white hover:bg-red-700 active:bg-red-800",
};

const sizeClasses = {
 sm: "px-3 py-1.5 text-sm",
 md: "px-4 py-2 text-base",
 lg: "px-6 py-3 text-lg",
};

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
 variant = "primary",
 size = "md",
 fullWidth = false,
 loading = false,
 disabled,
 className = "",
 children,
 ...props
}, ref) => {
 // Use secondary color for focus ring
 const focusRingColor = "focus:ring-store-secondary";
 
 const baseClasses = `
  font-medium transition-all duration-200
  focus:outline-none focus:ring-2 ${focusRingColor} focus:ring-offset-2
  disabled:opacity-50 disabled:cursor-not-allowed
  ${variantClasses[variant]}
  ${sizeClasses[size]}
  ${fullWidth ? "w-full" : ""}
  ${className}
 `.trim().replace(/\s+/g, " ");

 return (
  <button
   ref={ref}
   className={baseClasses}
   disabled={disabled || loading}
   {...props}
  >
   {loading ? (
    <span className="flex items-center justify-center">
     <svg
      className="animate-spin -ml-1 mr-2 h-4 w-4"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
     >
      <circle
       className="opacity-25"
       cx="12"
       cy="12"
       r="10"
       stroke="currentColor"
       strokeWidth="4"
      />
      <path
       className="opacity-75"
       fill="currentColor"
       d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
     </svg>
     Loading...
    </span>
   ) : (
    children
   )}
  </button>
 );
});

Button.displayName = "Button";

export default Button;

